// Copyright ©️ 2026 eVolve MEP, LLC

import { type Task, type RenderableUser, useEvolvePatch } from '@evolvemep/api-ts';
import { Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';

import { WrappedPopover } from 'components/Mantine/Popovers/WrappedPopover';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { validateUserNameOrEmail } from 'helpers/userHelpers';

type Props = {
  task: Pick<Task, 'taskId' | 'taskName' | 'assignees'> | null;
  user: RenderableUser | null;
  onConfirm: () => void;
  onCancel: () => void;
};

export const ConfirmAssignUserToTaskPopover = ({ task, user, onConfirm, onCancel }: Props) => {
  const { apiCall: patchTask } = useEvolvePatch(`shop/task/:taskId`);

  if (isNil(user)) return null;

  const onSave = async () => {
    if (isNil(task)) return;
    await patchTask(
      { assignees: [...task.assignees.map((a) => a.user.userId), user.userId] },
      {
        url: `shop/task/${task.taskId}`,
      },
    ).then((updatedTask) => {
      onConfirm();
      notifications.show({
        title: 'Successfully assigned',
        message: `${validateUserNameOrEmail(user)} assigned to task ${updatedTask.taskName}`,
        color: 'green',
      });
    });
  };

  return (
    <WrappedPopover
      title="Assign task"
      opened={isNotNil(task)}
      onConfirm={onSave}
      // The parent closes via its `onConfirm` callback; `onClose` here means "cancel"
      closeOnConfirm={false}
      onClose={onCancel}
      confirmationText="Assign"
      isDirty
      position="bottom-end"
    >
      <Text fz="sm">
        <b>{validateUserNameOrEmail(user)}</b> is not currently assigned to task <b>{task?.taskName}</b>. Are you sure
        you want to assign them to this task?
      </Text>
    </WrappedPopover>
  );
};
